import { type ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';

import {
  autorizarConvenioAr,
  desfazerRecusaAr,
  devolverEnderecoAr,
  fetchAutorizacoesAr,
  recusarEnvioAr,
  verificarPagamentoAr,
} from './actions';
import { comoEnvioDaTela, type ListaAutorizacoesAr, type ResumoAutorizacoesAr } from './dto';
import type { FalhaAutorizacaoAr } from './erros';
import type { EnvioParaDecidir } from './tipos';

/**
 * A fatia da ação de decisão que estes reducers leem, anotada à mão.
 *
 * As cinco decisões têm `Arg` diferentes (motivo, observação), então o laço vê um tipo
 * união; declarar o que o handler precisa — e só isso — é o que deixa o laço existir
 * sem `any` nem cinco blocos copiados.
 */
interface AcaoDeDecisao<Payload> {
  payload: Payload;
  meta: { arg: { solicitacaoId: string }; requestId: string };
}

interface AutorizacaoArState {
  envios: EnvioParaDecidir[];
  resumo: ResumoAutorizacoesAr;
  loading: boolean;
  /** Último `requestId` da leitura — descarta resposta de um processo que já saiu da tela. */
  requestId: string | null;
  /**
   * `solicitacaoId` → `requestId` da decisão em voo.
   *
   * Por SOLICITAÇÃO, e não um booleano de tela: o processo tem um card por requerido,
   * e o booleano global que morava aqui não era lido por ninguém — o duplo clique que
   * ele deveria impedir disparava duas mutações, e travar os dois cards por causa de
   * um seria pior ainda.
   */
  acaoEmVoo: Record<string, string>;
  /**
   * `requestId` da decisão mais recente. Só ela pode escrever a lista: sem isto, duas
   * decisões concorrentes terminam fora de ordem e a resposta da PRIMEIRA sobrescreve
   * a lista já atualizada pela segunda.
   */
  ultimaDecisaoRequestId: string | null;
  error: string | null;
  /**
   * Falha da LEITURA. Não vai para a tela — alimenta só o teto de erro do polling:
   * sem ele, um servidor fora do ar levaria uma releitura a cada 5–30 s pelos vinte
   * minutos inteiros do teto de relógio.
   */
  erroLeitura: string | null;
}

const RESUMO_VAZIO: ResumoAutorizacoesAr = { acionaveis: 0, vencidas: 0, comErroEmissao: 0 };

const initialState: AutorizacaoArState = {
  envios: [],
  resumo: RESUMO_VAZIO,
  loading: false,
  requestId: null,
  acaoEmVoo: {},
  ultimaDecisaoRequestId: null,
  error: null,
  erroLeitura: null,
};

function aplicarLista(state: AutorizacaoArState, lista: ListaAutorizacoesAr) {
  state.envios = (lista.items ?? []).map((dto) => ({
    envio: comoEnvioDaTela(dto),
    vencida: dto.vencida,
  }));
  state.resumo = lista.resumo ?? RESUMO_VAZIO;
}

/**
 * As cinco decisões compartilham reducers: todas devolvem a LISTA relida, e nenhuma
 * precisa de tratamento próprio. Registrá-las num laço deixa impossível uma nova
 * decisão entrar sem `acaoEmVoo`/`error` — que foi como o botão duplo-clicável nasceu
 * em telas anteriores.
 */
const DECISOES = [
  autorizarConvenioAr,
  devolverEnderecoAr,
  recusarEnvioAr,
  desfazerRecusaAr,
  verificarPagamentoAr,
];

function liberar(state: AutorizacaoArState, solicitacaoId: string, requestId: string) {
  // Só quem trancou destranca: um clique novo já substituiu o `requestId`, e deixar a
  // resposta velha limpar a trava reabriria o botão com a segunda chamada no ar.
  if (state.acaoEmVoo[solicitacaoId] === requestId) delete state.acaoEmVoo[solicitacaoId];
}

function registrarDecisoes(builder: ActionReducerMapBuilder<AutorizacaoArState>) {
  for (const decisao of DECISOES) {
    builder
      .addCase(decisao.pending, (state, action: AcaoDeDecisao<undefined>) => {
        state.acaoEmVoo[action.meta.arg.solicitacaoId] = action.meta.requestId;
        state.ultimaDecisaoRequestId = action.meta.requestId;
        state.error = null;
      })
      .addCase(decisao.fulfilled, (state, action: AcaoDeDecisao<ListaAutorizacoesAr>) => {
        liberar(state, action.meta.arg.solicitacaoId, action.meta.requestId);
        if (state.ultimaDecisaoRequestId !== action.meta.requestId) return;
        aplicarLista(state, action.payload);
      })
      .addCase(decisao.rejected, (state, action: AcaoDeDecisao<FalhaAutorizacaoAr | undefined>) => {
        liberar(state, action.meta.arg.solicitacaoId, action.meta.requestId);
        state.error = action.payload?.mensagem ?? 'Não foi possível registrar sua decisão';
        // O servidor disse qual é o estado agora — aplicá-lo é mais barato e mais
        // exato do que um refetch, que voltaria com a mesma informação um roundtrip
        // depois e ainda por cima poderia falhar.
        const estadoAtual = action.payload?.estadoAtual;
        if (!estadoAtual) return;
        const linha = state.envios.find(
          (e) => e.envio.solicitacaoId === action.meta.arg.solicitacaoId,
        );
        if (linha) linha.envio.estado = estadoAtual;
      });
  }
}

const autorizacaoArSlice = createSlice({
  name: 'autorizacaoAr',
  initialState,
  reducers: {
    limparAutorizacoesAr: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAutorizacoesAr.pending, (state, action) => {
        state.requestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAutorizacoesAr.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.erroLeitura = null;
        aplicarLista(state, action.payload);
      })
      .addCase(fetchAutorizacoesAr.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        // Lista vazia e SEM `error` na tela: o bloco de AR é um entre vários no detalhe
        // do processo, e quem abriu para ver outra coisa não pode levar um erro que não
        // pediu. Sem envios, a faixa e os cards simplesmente não renderizam — é também
        // o que acontece com a flag do backend desligada, que responde 404.
        state.envios = [];
        state.resumo = RESUMO_VAZIO;
        state.erroLeitura = action.payload?.mensagem ?? 'Erro ao carregar os envios de AR';
      });
    registrarDecisoes(builder);
  },
});

export const { limparAutorizacoesAr } = autorizacaoArSlice.actions;
export const autorizacaoArReducer = autorizacaoArSlice.reducer;
